/**
 * Provider brand marks.
 *
 * A "Continue with Google" button without the G is the one place a generic
 * button costs real trust: people scan for the mark, not the sentence, and its
 * absence reads as a phishing page rather than a minimal one. Both companies'
 * brand guidelines require the official mark on their sign-in buttons, so this
 * is also the compliant option, not merely the prettier one.
 *
 * Inlined as SVG rather than fetched: `img-src` does not include either CDN,
 * these never change, and a sign-in button whose icon is still loading is worse
 * than one drawn immediately.
 *
 * `aria-hidden` throughout — the button's own text already says which provider
 * it is, and a second announcement is noise.
 */
import type { Provider } from "./sign-in";

/**
 * Discord's wordless mark, in Blurple.
 *
 * Not currentColor. Google's G must be full colour under their brand terms, so
 * a monochrome Discord mark beside it looked like one of the two had failed to
 * load — the buttons read as inconsistent rather than restrained. #5865F2 is
 * Discord's primary brand colour and is what their own guidelines specify on a
 * light background.
 */
const DISCORD = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false" fill="#5865F2"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.011c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .079.01c.12.099.245.198.372.292a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.029ZM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.095 2.157 2.42 0 1.332-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.332-.946 2.418-2.157 2.418Z"/></svg>`;

/**
 * Google's four-colour G, at its official colours.
 *
 * Deliberately NOT currentColor: Google's brand terms forbid recolouring the
 * mark, and a monochrome G on a white button is the version people have learned
 * to distrust.
 */
const GOOGLE = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path fill="#4285F4" d="M23.52 12.273c0-.851-.076-1.67-.218-2.455H12v4.642h6.458a5.52 5.52 0 0 1-2.396 3.622v3.01h3.878c2.269-2.09 3.58-5.167 3.58-8.82Z"/><path fill="#34A853" d="M12 24c3.24 0 5.956-1.075 7.94-2.908l-3.877-3.01c-1.075.72-2.45 1.145-4.063 1.145-3.126 0-5.772-2.111-6.716-4.949H1.276v3.109A11.995 11.995 0 0 0 12 24Z"/><path fill="#FBBC05" d="M5.284 14.278a7.212 7.212 0 0 1 0-4.556V6.613H1.276a12.008 12.008 0 0 0 0 10.774l4.008-3.109Z"/><path fill="#EA4335" d="M12 4.773c1.762 0 3.343.606 4.587 1.795l3.44-3.44C17.951 1.19 15.235 0 12 0 7.31 0 3.256 2.688 1.276 6.613l4.008 3.109C6.228 6.884 8.874 4.773 12 4.773Z"/></svg>`;

const MARKS: Record<Provider, string> = { discord: DISCORD, google: GOOGLE };

export function providerMark(provider: Provider): string {
  return MARKS[provider] ?? "";
}
